import React from 'react';

import { Badge, Box, HoverCard, Stack, Text } from '@mantine/core';

import { CHECKLIST_SECTION_LABEL } from '@/utils/enrollmentChecklist';

import { RowChecklist, useRowChecklist } from '../hooks/useRowChecklist';

import styles from './cellsV2.module.scss';
import { ChecklistGate } from './ChecklistCells';

import type { ChecklistMissing, ChecklistSectionKey } from '@/utils/enrollmentChecklist';

interface SetupCellProps {
  patientId: string;
  status: string;
  /**
   * The row's shared checklist evaluation (see useRowChecklist). When omitted the cell runs its
   * own lazy check, as before.
   */
  checklist?: RowChecklist;
}

function groupMissing(
  missing: ChecklistMissing[],
  sectionLabels: Partial<Record<ChecklistSectionKey, string>>
): { key: ChecklistSectionKey; label: string; items: string[] }[] {
  const groups: { key: ChecklistSectionKey; label: string; items: string[] }[] = [];
  missing.forEach((row) => {
    let group = groups.find((entry) => entry.key === row.sectionKey);
    if (!group) {
      group = {
        key: row.sectionKey,
        label: sectionLabels[row.sectionKey] ?? CHECKLIST_SECTION_LABEL[row.sectionKey],
        items: []
      };
      groups.push(group);
    }
    group.items.push(row.label);
  });
  return groups;
}

export const SetupCell: React.FC<SetupCellProps> = ({ patientId, status, checklist }) => {
  // Only does work when no shared evaluation is passed in.
  const own = useRowChecklist(patientId, status, !checklist);
  const state = checklist ?? own.checklist;

  const renderContent = () => {
    const evaluation = state.evaluation;
    if (!evaluation) return null;
    if (evaluation.isComplete) {
      return (
        <Badge variant="light" color="teal" radius="sm" classNames={{ root: styles.statusBadge }}>
          Complete
        </Badge>
      );
    }
    const sectionLabels: Partial<Record<ChecklistSectionKey, string>> = {};
    evaluation.sections.forEach((section) => {
      sectionLabels[section.key] = section.label;
    });
    const groups = groupMissing(evaluation.missing, sectionLabels);
    return (
      <HoverCard width={260} shadow="md" position="bottom-start" withinPortal openDelay={100}>
        <HoverCard.Target>
          <Badge
            variant="light"
            color="yellow"
            radius="sm"
            classNames={{ root: styles.statusBadge }}
            style={{ cursor: 'default' }}>
            {evaluation.missing.length} missing
          </Badge>
        </HoverCard.Target>
        <HoverCard.Dropdown>
          <Stack gap={8}>
            {groups.map((group) => (
              <Box key={group.key}>
                <Text fz={12} fw={600}>
                  {group.label}
                </Text>
                {group.items.map((label) => (
                  <Text key={label} fz={12} c="dimmed">
                    {label}
                  </Text>
                ))}
              </Box>
            ))}
          </Stack>
        </HoverCard.Dropdown>
      </HoverCard>
    );
  };

  return (
    <Box
      ref={checklist ? undefined : own.ref}
      onClick={(event: React.MouseEvent) => event.stopPropagation()}
      style={{ display: 'inline-flex', alignItems: 'center', minHeight: 20 }}>
      <ChecklistGate checklist={state}>{renderContent()}</ChecklistGate>
    </Box>
  );
};
